import {
  type ActivityPedagogicalCustomFieldResponse,
  PedagogicalFieldGroup,
  PedagogicalFieldType,
  type PedagogicalFieldType as PedagogicalFieldTypeValue,
  type UpdateActivityPedagogicalCustomFieldRequest,
} from '@api';

import { ACTIVITY_EDIT_PEDAGOGICAL_CUSTOM_I18N_PREFIX } from './activity-edit-pedagogical-custom.constants';
import { pedagogicalFieldHasDisplayValue } from './activity-edit-pedagogical.utils';

export const CUSTOM_PEDAGOGICAL_FIELD_MIN_ENUM_OPTIONS = 2;

const CUSTOM_PEDAGOGICAL_FIELD_TYPE_LABEL_SUFFIX: Record<PedagogicalFieldTypeValue, string> = {
  [PedagogicalFieldType.TEXT_INPUT]: 'TYPE_TEXT_INPUT',
  [PedagogicalFieldType.TEXT_AREA]: 'TYPE_TEXT_AREA',
  [PedagogicalFieldType.BOOLEAN]: 'TYPE_BOOLEAN',
  [PedagogicalFieldType.ENUM]: 'TYPE_ENUM',
  [PedagogicalFieldType.HTML]: 'TYPE_HTML',
};

export interface CustomPedagogicalReorderMove {
  readonly fieldId: string;
  readonly ids: readonly string[];
  readonly position: number;
  readonly total: number;
}

export interface CustomPedagogicalReorderRow {
  readonly id: string;
}

/**
 * Applies a metadata PATCH body onto one custom field row (type is immutable).
 *
 * @param field - Current draft row.
 * @param body - Metadata update from the gear menu.
 * @returns Row with updated name, description, and enum options when present in `body`.
 */
export function applyCustomPedagogicalFieldMetadataUpdate(
  field: ActivityPedagogicalCustomFieldResponse,
  body: UpdateActivityPedagogicalCustomFieldRequest,
): ActivityPedagogicalCustomFieldResponse {
  return {
    ...field,
    fieldName: body.fieldName ?? field.fieldName,
    description: patchCustomPedagogicalFieldDescription(field.description, body.description),
    options: body.options ?? field.options,
  };
}

/**
 * Detail panel rows: visible custom fields with a displayable value, in `sortOrder`.
 *
 * @param items - Full custom field list from the instance.
 * @returns Visible fields that have a non-empty display value, sorted by `sortOrder`.
 */
export function customFieldsForDetailPanel(
  items: readonly ActivityPedagogicalCustomFieldResponse[],
): ActivityPedagogicalCustomFieldResponse[] {
  return orderedCustomPedagogicalFields(items).filter(
    (field) => field.visible && customPedagogicalFieldHasDisplayValue(field),
  );
}

/**
 * Custom gear-menu id order after a CDK drop within the custom-only block.
 *
 * @param rows - Custom rows in current menu order.
 * @param previousIndex - Index before the drop.
 * @param currentIndex - Index after the drop.
 * @returns Reordered field ids, or `null` when the drop is a no-op or out of range.
 */
export function customMenuIdsAfterDrop(
  rows: readonly CustomPedagogicalReorderRow[],
  previousIndex: number,
  currentIndex: number,
): string[] | null {
  if (previousIndex === currentIndex) {
    return null;
  }

  const ids = rows.map((row) => row.id);
  if (previousIndex < 0 || currentIndex < 0 || previousIndex >= ids.length || currentIndex >= ids.length) {
    return null;
  }

  return moveIds(ids, previousIndex, currentIndex);
}

/**
 * Custom gear-menu id order after ArrowUp (`-1`) or ArrowDown (`1`).
 *
 * @param rows - Custom rows in current menu order.
 * @param fieldId - Id of the row being moved.
 * @param direction - `-1` for up, `1` for down.
 * @returns Move metadata including the new id order, or `null` when the move is invalid.
 */
export function customMenuIdsAfterKeyMove(
  rows: readonly CustomPedagogicalReorderRow[],
  fieldId: string,
  direction: -1 | 1,
): CustomPedagogicalReorderMove | null {
  const ids = rows.map((row) => row.id);
  const from = ids.indexOf(fieldId);
  const to = from + direction;
  if (from === -1 || to < 0 || to >= ids.length) {
    return null;
  }

  const nextIds = moveIds(ids, from, to);
  return { fieldId, ids: nextIds, position: to + 1, total: nextIds.length };
}

/**
 * Whether a stored custom field value should render on the read-only detail panel.
 *
 * @param field - Custom field row from the API.
 * @returns `true` when the value is non-empty for the field's type.
 */
export function customPedagogicalFieldHasDisplayValue(field: ActivityPedagogicalCustomFieldResponse): boolean {
  return pedagogicalFieldHasDisplayValue({
    fieldName: field.id,
    type: field.type,
    group: PedagogicalFieldGroup.SYSTEM,
    value: field.value,
    visible: field.visible,
    sortOrder: field.sortOrder,
  });
}

/** @deprecated Use {@link pedagogicalFieldTypeMenuIcon} from `activity-edit-pedagogical.utils`. */
export { pedagogicalFieldTypeMenuIcon as customPedagogicalFieldMenuIcon } from './activity-edit-pedagogical.utils';

/**
 * Transloco key (under `ACTIVITY_BROWSER`) for a custom field type display name.
 *
 * @param type - Wire `PedagogicalFieldType`.
 * @returns Key relative to the activity-browser scope, e.g. `DETAIL.EDIT_PAGE.CUSTOM_FIELDS.TYPE_TEXT_INPUT`.
 */
export function customPedagogicalFieldTypeLabelKey(type: PedagogicalFieldTypeValue): string {
  return `${ACTIVITY_EDIT_PEDAGOGICAL_CUSTOM_I18N_PREFIX}.${CUSTOM_PEDAGOGICAL_FIELD_TYPE_LABEL_SUFFIX[type]}`;
}

/**
 * @param options - Enum option labels from the API.
 * @returns Comma-separated text for the gear-menu edit form.
 */
export function formatCustomEnumOptionsForEdit(options: readonly string[] | null | undefined): string {
  return (options ?? []).join(', ');
}

/**
 * Merges a server custom-field list into the draft while preserving in-flight value edits by id.
 *
 * @param current - Draft custom fields before the server response.
 * @param serverItems - Full list returned by list or config endpoints.
 * @returns Merged list sorted by `sortOrder`, keeping draft values where ids match.
 */
export function mergeCustomPedagogicalFieldListFromServer(
  current: readonly ActivityPedagogicalCustomFieldResponse[],
  serverItems: readonly ActivityPedagogicalCustomFieldResponse[],
): ActivityPedagogicalCustomFieldResponse[] {
  const valueById = new Map(current.map((item) => [item.id, item.value]));
  return orderedCustomPedagogicalFields(serverItems).map((item) => ({
    ...item,
    value: valueById.has(item.id) ? (valueById.get(item.id) ?? item.value) : item.value,
  }));
}

/**
 * Applies one value-save response onto the draft list without dropping other custom rows.
 *
 * @param current - Draft custom fields before the value save response.
 * @param serverRow - Row returned by `PUT …/value`.
 * @param committedValue - Value the client sent on that save (guards stale responses).
 * @returns Updated list sorted by `sortOrder`.
 */
export function mergeCustomPedagogicalFieldRowFromServer(
  current: readonly ActivityPedagogicalCustomFieldResponse[],
  serverRow: ActivityPedagogicalCustomFieldResponse,
  committedValue: string | null,
): ActivityPedagogicalCustomFieldResponse[] {
  return orderedCustomPedagogicalFields(
    current.map((item) => {
      if (item.id !== serverRow.id || item.value !== committedValue) {
        return item;
      }

      return { ...item, ...serverRow, value: serverRow.value };
    }),
  );
}

/**
 * Custom pedagogical fields in ascending `sortOrder` (gear menu and editor).
 *
 * @param fields - Custom fields in any order.
 * @returns New array sorted by `sortOrder`.
 */
export function orderedCustomPedagogicalFields(
  fields: readonly ActivityPedagogicalCustomFieldResponse[],
): ActivityPedagogicalCustomFieldResponse[] {
  return fields.toSorted((left, right) => left.sortOrder - right.sortOrder);
}

/**
 * Parses a comma-separated option list for `enum` custom fields.
 *
 * @param raw - Raw text from the create form.
 * @returns Unique trimmed option labels in first-seen order.
 */
export function parseEnumOptionsFromCommaSeparated(raw: string): string[] {
  const parts = raw
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
  return [...new Set(parts)];
}

/**
 * Visible custom fields for the main editor form (KT-1081).
 *
 * @param fields - Full custom field list from the draft.
 * @returns Visible rows sorted by `sortOrder`.
 */
export function visibleCustomFieldsForEditor(
  fields: readonly ActivityPedagogicalCustomFieldResponse[],
): ActivityPedagogicalCustomFieldResponse[] {
  return orderedCustomPedagogicalFields(fields).filter((field) => field.visible);
}

/**
 * Optimistic reorder within the custom-field list (`sortOrder` 1…n).
 *
 * @param items - Custom fields before the move.
 * @param fieldId - Id of the row being moved.
 * @param position - Target position in the custom-only list (1-based).
 * @returns Copy of `items` with updated `sortOrder` values.
 */
export function withCustomPedagogicalFieldAtPosition(
  items: readonly ActivityPedagogicalCustomFieldResponse[],
  fieldId: string,
  position: number,
): ActivityPedagogicalCustomFieldResponse[] {
  const ordered = orderedCustomPedagogicalFields(items);
  const from = ordered.findIndex((item) => item.id === fieldId);
  if (from === -1 || position < 1 || position > ordered.length) {
    return ordered.map((item) => ({ ...item }));
  }

  const to = position - 1;
  if (from === to) {
    return ordered.map((item) => ({ ...item }));
  }

  const ids = moveIds(
    ordered.map((item) => item.id),
    from,
    to,
  );
  const sortOrderById = new Map(ids.map((id, index) => [id, index + 1]));

  const updated = ordered.map((item) => {
    const sortOrder = sortOrderById.get(item.id);
    return sortOrder === undefined || sortOrder === item.sortOrder ? item : { ...item, sortOrder };
  });
  return orderedCustomPedagogicalFields(updated);
}

/**
 * Moves one id within a list (used by gear-menu reorder helpers).
 *
 * @param ids - Id list in current order.
 * @param from - Index to move from.
 * @param to - Index to insert at.
 * @returns New id array with one element relocated.
 */
function moveIds(ids: readonly string[], from: number, to: number): string[] {
  const next = [...ids];
  const moved = next[from];
  next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
}

/**
 * @param current - Description on the draft row before the patch.
 * @param patch - `undefined` leaves `current`; `null` clears; a string replaces the value.
 * @returns The description to store on the row after the patch.
 */
function patchCustomPedagogicalFieldDescription(
  current: string | null,
  patch: string | null | undefined,
): string | null {
  if (patch === undefined) {
    return current;
  }

  return patch;
}
